/**
 * Copyright (c) Meta Platforms, Inc. and affiliates.
 *
 * This source code is licensed under the MIT license found in the
 * LICENSE file in the root directory of this source tree.
 *
 */
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import { $wrapNodeInElement, mergeRegister } from '@lexical/utils';
import {
  $createParagraphNode,
  $createRangeSelection,
  $getSelection,
  $insertNodes,
  $isNodeSelection,
  $isRootOrShadowRoot,
  $setSelection,
  COMMAND_PRIORITY_EDITOR,
  COMMAND_PRIORITY_HIGH,
  COMMAND_PRIORITY_LOW,
  createCommand,
  DRAGOVER_COMMAND,
  DRAGSTART_COMMAND,
  DROP_COMMAND,
  LexicalCommand,
  LexicalEditor,
} from 'lexical';
import * as React from 'react';
import { useEffect, useRef, useState } from 'react';
import { CAN_USE_DOM } from 'shared/canUseDOM';
import {
  $createImageNode,
  $isImageNode,
  ImageNode,
  ImagePayload,
} from '../../nodes/ImageNode';
import {
  Button,
  ButtonColor,
  ButtonWidth,
} from '../../../../../../components/uikit/buttons/Button.tsx';
import commonStyles from '../commonStyles.module.scss';
import styles from '../commonStyles.module.scss';
import { useScopedTranslation } from '../../../../../../application/localization/useScopedTranslation.ts';
import { Input } from '../../../../../../components/uikit/inputs/Input.tsx';
import clsx from 'clsx';
import CloseIcon from 'assets/icons/close-button-small.svg?react';

export type InsertImagePayload = Readonly<ImagePayload>;

const getDOMSelection = (targetWindow: Window | null): Selection | null =>
  CAN_USE_DOM ? (targetWindow || window).getSelection() : null;

export const INSERT_IMAGE_COMMAND: LexicalCommand<InsertImagePayload> =
  createCommand('INSERT_IMAGE_COMMAND');

export function InsertImageUriDialogBody({
  onClick,
  onClose,
}: {
  onClick: (payload: InsertImagePayload) => void;
  onClose: () => void;
}) {
  const [src, setSrc] = useState('');
  const [altText, setAltText] = useState('');

  const i18n = useScopedTranslation('Page.PageDesigner');

  const isDisabled = src === '';

  return (
    <div>
      <Input
        onChange={(e) => setSrc(e.target.value)}
        value={src}
        topHelperText={i18n.t('image_url')}
        containerClassName={clsx(
          commonStyles.marginBottom35,
          commonStyles.marginTop20,
        )}
        fullWidth={true}
      />
      <Input
        onChange={(e) => setAltText(e.target.value)}
        value={altText}
        topHelperText={i18n.t('alt_text')}
        fullWidth={true}
      />
      <div className={styles.buttonContainer}>
        <Button
          onClick={onClose}
          title={i18n.t('cancel')}
          color={ButtonColor.Secondary}
        />
        <Button
          onClick={() => onClick({ altText, src })}
          title={i18n.t('insert_image')}
          color={ButtonColor.Primary}
          disabled={isDisabled}
        />
      </div>
    </div>
  );
}

export function InsertImageUploadedDialogBody({
  onClick,
  onClose,
}: {
  onClick: (payload: InsertImagePayload) => void;
  onClose: () => void;
}) {
  const [src, setSrc] = useState('');
  const [altText, setAltText] = useState('');
  const [imageName, setImageName] = useState('');

  const uploadFileRef = useRef<HTMLInputElement>(null);
  const triggerFileUpload = () => {
    uploadFileRef.current!.click();
  };

  const i18n = useScopedTranslation('Page.PageDesigner');

  const isDisabled = src === '';

  const loadImage = (files: FileList | null) => {
    const reader = new FileReader();
    reader.onload = function () {
      if (typeof reader.result === 'string') {
        setSrc(reader.result);
        if (files) {
          setImageName(files[0]?.name);
        }
      }
      return '';
    };
    if (files?.length) {
      reader.readAsDataURL(files[0]);
    }
  };

  const deleteImage = () => {
    setSrc('');
    setImageName('');
  };

  return (
    <>
      <input
        type="file"
        id="uploadFile"
        accept="image/*"
        ref={uploadFileRef}
        style={{ display: 'none' }}
        onChange={(e) => loadImage(e.target.files)}
      />
      <div className={clsx(styles.greyText, styles.marginBottom5)}>
        {i18n.t('upload_image')}
      </div>
      <div
        className={clsx(
          styles.rowContainer,
          styles.spaceBetween,
          styles.alignCenter,
          styles.marginBottom50,
        )}
      >
        {imageName ? (
          <div className={styles.rowContainer}>
            <div className={styles.regularText}>{imageName}</div>
            <CloseIcon className={styles.closeIcon} onClick={deleteImage} />
          </div>
        ) : (
          <div className={styles.greyText}>{i18n.t('no_image_uploaded')}</div>
        )}
        <Button
          title={i18n.t('select_file')}
          color={ButtonColor.Secondary}
          onClick={triggerFileUpload}
        />
      </div>
      <Input
        onChange={(e) => setAltText(e.target.value)}
        value={altText}
        topHelperText={i18n.t('alt_text')}
        fullWidth={true}
      />
      <div className={styles.buttonContainer}>
        <Button
          onClick={onClose}
          title={i18n.t('cancel')}
          color={ButtonColor.Secondary}
        />
        <Button
          title={i18n.t('insert_image')}
          color={ButtonColor.Primary}
          disabled={isDisabled}
          onClick={() => onClick({ altText, src })}
        />
      </div>
    </>
  );
}

export function InsertImageDialog({
  activeEditor,
  onClose,
}: {
  activeEditor: LexicalEditor;
  onClose: () => void;
}): React.JSX.Element {
  const [mode, setMode] = useState<null | 'url' | 'file'>(null);
  const hasModifier = useRef(false);
  const i18n = useScopedTranslation('Page.PageDesigner');

  useEffect(() => {
    hasModifier.current = false;
    const handler = (e: KeyboardEvent) => {
      hasModifier.current = e.altKey;
    };
    document.addEventListener('keydown', handler);
    return () => {
      document.removeEventListener('keydown', handler);
    };
  }, [activeEditor]);

  const onClick = (payload: InsertImagePayload) => {
    activeEditor.dispatchCommand(INSERT_IMAGE_COMMAND, payload);
    onClose();
  };

  return (
    <>
      {!mode && (
        <>
          <Button
            onClick={() => setMode('url')}
            title={i18n.t('url')}
            color={ButtonColor.Secondary}
            width={ButtonWidth.Fullwidth}
            className={commonStyles.marginBottom10}
          />
          <Button
            onClick={() => setMode('file')}
            title={i18n.t('file')}
            color={ButtonColor.Secondary}
            width={ButtonWidth.Fullwidth}
          />
        </>
      )}
      {mode === 'url' && (
        <InsertImageUriDialogBody onClick={onClick} onClose={onClose} />
      )}
      {mode === 'file' && (
        <InsertImageUploadedDialogBody onClick={onClick} onClose={onClose} />
      )}
    </>
  );
}

export default function ImagesPlugin({
  captionsEnabled,
}: {
  captionsEnabled?: boolean;
}): React.JSX.Element | null {
  const [editor] = useLexicalComposerContext();

  useEffect(() => {
    if (!editor.hasNodes([ImageNode])) {
      throw new Error('ImagesPlugin: ImageNode not registered on editor');
    }

    return mergeRegister(
      editor.registerCommand<InsertImagePayload>(
        INSERT_IMAGE_COMMAND,
        (payload) => {
          const imageNode = $createImageNode(payload);
          $insertNodes([imageNode]);
          if ($isRootOrShadowRoot(imageNode.getParentOrThrow())) {
            $wrapNodeInElement(imageNode, $createParagraphNode).selectEnd();
          }

          return true;
        },
        COMMAND_PRIORITY_EDITOR,
      ),
      editor.registerCommand<DragEvent>(
        DRAGSTART_COMMAND,
        (event) => {
          return $onDragStart(event);
        },
        COMMAND_PRIORITY_HIGH,
      ),
      editor.registerCommand<DragEvent>(
        DRAGOVER_COMMAND,
        (event) => {
          return $onDragover(event);
        },
        COMMAND_PRIORITY_LOW,
      ),
      editor.registerCommand<DragEvent>(
        DROP_COMMAND,
        (event) => {
          return $onDrop(event, editor);
        },
        COMMAND_PRIORITY_HIGH,
      ),
    );
  }, [captionsEnabled, editor]);

  return null;
}

const TRANSPARENT_IMAGE =
  'data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///yH5BAEAAAAALAAAAAABAAEAAAIBRAA7';
const img = document.createElement('img');
img.src = TRANSPARENT_IMAGE;

function $onDragStart(event: DragEvent): boolean {
  const node = $getImageNodeInSelection();
  if (!node) {
    return false;
  }
  const dataTransfer = event.dataTransfer;
  if (!dataTransfer) {
    return false;
  }
  dataTransfer.setData('text/plain', '_');
  dataTransfer.setDragImage(img, 0, 0);
  dataTransfer.setData(
    'application/x-lexical-drag',
    JSON.stringify({
      data: {
        altText: node.__altText,
        caption: node.__caption,
        height: node.__height,
        key: node.getKey(),
        maxWidth: node.__maxWidth,
        showCaption: node.__showCaption,
        src: node.__src,
        width: node.__width,
      },
      type: 'image',
    }),
  );

  return true;
}

function $onDragover(event: DragEvent): boolean {
  const node = $getImageNodeInSelection();
  if (!node) {
    return false;
  }
  if (!canDropImage(event)) {
    event.preventDefault();
  }
  return true;
}

function $onDrop(event: DragEvent, editor: LexicalEditor): boolean {
  const node = $getImageNodeInSelection();
  if (!node) {
    return false;
  }
  const data = getDragImageData(event);
  if (!data) {
    return false;
  }
  event.preventDefault();
  if (canDropImage(event)) {
    const range = getDragSelection(event);
    node.remove();
    const rangeSelection = $createRangeSelection();
    if (range !== null && range !== undefined) {
      rangeSelection.applyDOMRange(range);
    }
    $setSelection(rangeSelection);
    editor.dispatchCommand(INSERT_IMAGE_COMMAND, data);
  }
  return true;
}

function $getImageNodeInSelection(): ImageNode | null {
  const selection = $getSelection();
  if (!$isNodeSelection(selection)) {
    return null;
  }
  const nodes = selection.getNodes();
  const node = nodes[0];
  return $isImageNode(node) ? node : null;
}

function getDragImageData(event: DragEvent): null | InsertImagePayload {
  const dragData = event.dataTransfer?.getData('application/x-lexical-drag');
  if (!dragData) {
    return null;
  }
  const { type, data } = JSON.parse(dragData);
  if (type !== 'image') {
    return null;
  }

  return data;
}

declare global {
  interface DragEvent {
    rangeOffset?: number;
    rangeParent?: Node;
  }
}

function canDropImage(event: DragEvent): boolean {
  const target = event.target;
  return !!(
    target &&
    target instanceof HTMLElement &&
    !target.closest('code, span.editor-image') &&
    target.parentElement &&
    target.parentElement.closest('div.ContentEditable__root')
  );
}

function getDragSelection(event: DragEvent): Range | null | undefined {
  let range;
  const target = event.target as null | Element | Document;
  const targetWindow =
    target == null
      ? null
      : target.nodeType === 9
        ? (target as Document).defaultView
        : (target as Element).ownerDocument.defaultView;
  const domSelection = getDOMSelection(targetWindow);
  if (document.caretRangeFromPoint) {
    range = document.caretRangeFromPoint(event.clientX, event.clientY);
  } else if (event.rangeParent && domSelection !== null) {
    domSelection.collapse(event.rangeParent, event.rangeOffset || 0);
    range = domSelection.getRangeAt(0);
  } else {
    throw Error(`Cannot get the selection when dragging`);
  }

  return range;
}
